
import React, { useEffect } from "react";
import { Navigate, useLocation, createBrowserRouter, Outlet } from "react-router-dom";

import { useAPI } from "services/Api";
import ChangePassword from "pages/ChangePassword";
import EditCoachingPlan from "planEditor/EditCoachingPlan";
import PatientEditor from "pages/PatientEditor";
import EditUser from "pages/EditUser";
import ExternalReports from "pages/ExternalReports";
import Gallery from "pages/Gallery";
import Login from "pages/Login";
import Maintenance from "pages/Maintenance";
import PageNotFound from "pages/PageNotFound";
import PatientUsage from "usage/PatientUsage";
import PatientReportReview from "pages/PatientReportReview";
import ReportAssessmentValidator from "reports/ReportAssessmentValidator";
import ReportProcessor from "reports/ReportProcessor";
import ReportReview from "pages/ReportReview";
import ResetPassword from "pages/ResetPassword";
import ServiceUnavailable from "./pages/ServiceUnavailable";
import Sessions from "sessions/Sessions";
import SetPassword from "pages/SetPassword";
import UserSettings from "pages/UserSettings";
import ValidateEmail from "pages/ValidateEmail";

import { CustomRouteObject } from "types/CustomRouteObject";
import { circuitBreaker } from "circuitbreaker/CircuitBreaker";
import { isLoggedIn } from "services/AccountServices";
import { mcpClientService } from "mcp/services/MCPClientService";
import { sessionManager } from "utility/SessionManager";

// App wrapper component that can use hooks for global functionality
const AppWrapper: React.FC = () => {
    const API = useAPI(); // Now this is properly inside a React component

    useEffect(() => {
        const originalConsoleError = console.error;
        console.error = (...args) => {
            originalConsoleError(...args);

            // Skip sending /diagnostics Slack notifications for errors emitted by:
            //   -- vite build tool
            if (args?.length > 0 && typeof args[0] === "string" && args[0] === "[vite]") {
                return;
            }

            void API.api_Diagnostics("[console.error]", { message: args });
        };

        return () => {
            console.error = originalConsoleError;
        };
    }, [API]);

    return <Outlet />; // This renders the matched route component
};

// Initialize app-level services (this runs at module level, which is fine)
console.log("[App] Resetting circuit breaker on app initialization");
circuitBreaker.forceReset();

if (isLoggedIn() && !sessionManager.isSessionInitialized()) {
    console.log("[App] User already logged in on app start. Initializing session.");
    sessionManager.initialize();

    // Auto-connect using default MCP server configuration
    if (mcpClientService.isEnabled()) {
        mcpClientService.connect().catch(error => {
            console.warn("[App] MCP auto-connection failed at startup:", error);
        });
    }
}

const PublicRouteElement = ({ component: Component }) => {
    if (!Component) return <PageNotFound />;
    return <Component />;
};

const ProtectedRouteElement = ({ component: Component }) => {
    const location = useLocation();
    const loggedIn = isLoggedIn();

    if (!Component) return <PageNotFound />;

    if (!loggedIn) {
        return <Navigate to="/login" state={{ from: location.pathname }} replace />;
    }

    return <Component />;
};

export const routeConfig: CustomRouteObject[] = [
    // Public Routes
    { path: "/account-confirm-email/:key/", element: <PublicRouteElement component={ValidateEmail}/>, requiresAuth: false },
    { path: "/login", element: <PublicRouteElement component={Login}/>, requiresAuth: false },
    { path: "/maintenance", element: <PublicRouteElement component={Maintenance}/>, requiresAuth: false },
    { path: "/password/reset", element: <PublicRouteElement component={ResetPassword}/>, requiresAuth: false },
    { path: "/password/reset/confirm", element: <PublicRouteElement component={SetPassword}/>, requiresAuth: false },
    { path: "/password/reset/confirm/:uid/:token", element: <PublicRouteElement component={SetPassword}/>, requiresAuth: false },

    // Protected Routes
    { path: "/create-patient", element: <ProtectedRouteElement component={PatientEditor}/>, requiresAuth: true },
    { path: "/outcomes/:reportId/:generate", element: <ProtectedRouteElement component={ReportProcessor}/>, requiresAuth: true },
    { path: "/password/change", element: <ProtectedRouteElement component={ChangePassword}/>, requiresAuth: true },
    { path: "/patient/:id/usage", element: <ProtectedRouteElement component={PatientUsage}/>, requiresAuth: true },
    { path: "/patient/:id/edit", element: <ProtectedRouteElement component={PatientEditor}/>, requiresAuth: true },
    { path: "/patient/:id/coaching-plan", element: <ProtectedRouteElement component={EditCoachingPlan}/>, requiresAuth: true },
    { path: "/patient/:id/reports", element: <ProtectedRouteElement component={PatientReportReview}/>, requiresAuth: true },
    { path: "/patient/:id/outcomes/:generate", element: <ProtectedRouteElement component={ReportProcessor}/>, requiresAuth: true },
    { path: "/reports", element: <ProtectedRouteElement component={ReportReview}/>, requiresAuth: true },
    { path: "/reports/validate", element: <ProtectedRouteElement component={ReportAssessmentValidator}/>, requiresAuth: true },
    { path: "/reportsExternal", element: <ProtectedRouteElement component={ExternalReports}/>, requiresAuth: true },
    { path: "/serviceunavailable", element: <ProtectedRouteElement component={ServiceUnavailable}/>, requiresAuth: true },
    { path: "/sessions", element: <ProtectedRouteElement component={Sessions}/>, requiresAuth: true },
    { path: "/user", element: <ProtectedRouteElement component={UserSettings}/>, requiresAuth: true },
    { path: "/user/edit", element: <ProtectedRouteElement component={EditUser}/>, requiresAuth: true },
    { path: "/", element: <ProtectedRouteElement component={Gallery}/>, requiresAuth: true },

    // Default redirect
    { path: "*", element: <PageNotFound />, requiresAuth: false }
];

// Create the router with AppWrapper as the root element
export const router = createBrowserRouter([
    {
        path: "/",
        element: <AppWrapper />, // This handles global functionality with hooks
        children: routeConfig.map(route => ({
            path: route.path,
            element: route.element
        }))
    }
]);

export default AppWrapper;
